(() => {
  'use strict';
  if (window.sdBenefits) return;
  const configNode = document.querySelector('[data-sd-benefit-config]');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const rewards = config.rewards || {};
  const ids = { white_glove: 50251221958876, 90: 40067493363871, 150: 40067494314143, 200: 45745667539164, 300: 45745667670236, 350: 67573719105756, 500: 67573720645852, 750: 67573721497820 };
  const vexIds = Object.fromEntries(Object.entries(rewards.vex_marker_variants || {}).filter(([value, id]) => Number(value) > 0 && Number(id) > 0).map(([value, id]) => [Number(value), Number(id)]));
  const historicalVexIds = [68020879229148, 68020879360220, 68020879425756, 68020879917276, 68020880048348];
  const vexManagedIds = new Set([...Object.values(vexIds), ...historicalVexIds]);
  const managedIds = new Set([...Object.values(ids), ...vexManagedIds]);
  const root = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve(), pending = 0, unresolved = false, checkoutPass = false;
  const managed = item => managedIds.has(Number(item.variant));
  const benefitId = (type, value) => type === 'white_glove' ? ids.white_glove : type === 'vex' ? vexIds[value] : ids[value];
  const benefitType = item => Number(item.variant) === ids.white_glove ? 'white_glove' : vexManagedIds.has(Number(item.variant)) ? 'vex' : 'gift';
  const selected = new Map();
  let latestCart = null;

  function campaignActive() {
    if (rewards.schema_version !== 1 || rewards.currency !== 'GBP' || rewards.basis !== 'per_qualifying_unit' || rewards.vex_enabled !== true) return false;
    const now = Date.now();
    for (const [key, before] of [['start_at', true], ['end_at', false]]) {
      if (!rewards[key]) continue;
      const date = Date.parse(rewards[key]);
      if (!Number.isFinite(date) || (before ? now < date : now > date)) return false;
    }
    return true;
  }

  function allowed(type, item, data = {}, display = false) {
    if (data.discounted === true) return false;
    const finance = data.financeChosen === true || item.financeChosen === true;
    if (type === 'white_glove') return item.wgAllowed === true || (item.wgAllowed === undefined && item.value > 0);
    if (finance) return false;
    if (type === 'gift') return item.value > 0 && item.giftAllowed !== false && Boolean(ids[item.value]);
    return type === 'vex' && item.vexValue > 0 && Boolean(vexIds[item.vexValue]) &&
      ((item.vexAllowed === true && campaignActive()) || (display && config.vexPreview === true && item.vexPreviewAllowed === true));
  }

  function label(type, value, item = {}) {
    if (type === 'white_glove') return item.deliveryLabel || config.delivery_label;
    return String(type === 'vex' ? config.vex_label : config.gift_label).replace('{value}', value);
  }

  async function readResponse(path, options = {}, format = 'json') {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(root + path, { ...options, signal: controller.signal });
      const data = await response[format]();
      return { response, data };
    } catch (error) {
      if (error.name === 'AbortError') throw new Error(config.unconfirmed);
      throw error;
    } finally { clearTimeout(timer); }
  }

  async function request(path, body) {
    const { response, data } = await readResponse(path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { cache: 'no-store' });
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || config.failed);
    return data;
  }

  async function state() {
    const { response, data: html } = await readResponse('cart?section_id=sd-benefit-state', { cache: 'no-store' }, 'text');
    if (!response.ok) throw new Error(config.unconfirmed);
    const documentState = new DOMParser().parseFromString(html, 'text/html');
    const node = documentState.querySelector('[data-sd-benefit-state]');
    if (!node) throw new Error(config.unconfirmed);
    const data = JSON.parse(node.textContent);
    if (!Array.isArray(data.items)) throw new Error(config.unconfirmed);
    const decoder = document.createElement('textarea');
    const decode = value => { decoder.innerHTML = String(value); return decoder.value; };
    data.items.forEach(item => {
      const properties = Array.isArray(item.properties) ? item.properties : Object.entries(item.properties || {});
      item.properties = Object.fromEntries(properties.map(([key, value]) => [decode(key), decode(value)]));
      item.title = decode(item.title || '');
    });
    await Promise.all(data.items.filter(item => !managed(item)).map(async item => {
      item.ref = await itemReference(item.variant, item.properties, item.selling_plan);
    }));
    latestCart = data;
    return data;
  }

  function canonical(properties) {
    return JSON.stringify(Object.entries(properties || {})
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => [key, String(value)])
      .sort(([a], [b]) => a.localeCompare(b)));
  }

  async function itemReference(variant, properties, sellingPlan) {
    const value = JSON.stringify([Number(variant), String(sellingPlan || ''), canonical(properties)]);
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
  }

  function gate() {
    document.documentElement.classList.toggle('sd-benefit-busy', pending > 0);
    document.documentElement.classList.toggle('sd-benefit-unconfirmed', unresolved);
    document.querySelectorAll('[data-cart-checkout-button]').forEach(button => {
      if (pending) { button.dataset.sdBenefitBlocked = 'true'; button.disabled = true; }
      else if (button.dataset.sdBenefitBlocked) { delete button.dataset.sdBenefitBlocked; button.disabled = false; }
    });
  }

  function showError(message) {
    unresolved = true;
    let notice = document.querySelector('[data-sd-benefit-error]');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'sd-benefit-error';
      notice.dataset.sdBenefitError = '';
      notice.setAttribute('role', 'alert');
      const copy = document.createElement('p');
      const button = document.createElement('button');
      button.type = 'button'; button.textContent = config.retry;
      button.addEventListener('click', () => run(async () => { await reconcile(); await refresh(); }).catch(() => {}));
      const continueButton = document.createElement('button');
      continueButton.type = 'button';
      continueButton.textContent = config.continue_without_benefits;
      continueButton.addEventListener('click', () => withoutBenefits().then(() => {
        window.location.assign(root + 'checkout');
      }).catch(() => {}));
      notice.append(copy, button, continueButton);
      (document.querySelector('[data-cart-checkout-buttons]') || document.querySelector('[data-form-wrapper]') || document.body).prepend(notice);
    }
    notice.querySelector('p').textContent = message;
    document.querySelectorAll('[data-sd-benefit-status]').forEach(node => { node.hidden = false; node.textContent = message; });
    gate();
  }

  function run(action) {
    pending++; gate();
    const operation = queue.then(action).then(result => {
      unresolved = false;
      document.querySelector('[data-sd-benefit-error]')?.remove();
      return result;
    }).catch(error => { error.description = error.message || config.unconfirmed; showError(error.description); throw error; }).finally(() => { pending--; gate(); });
    queue = operation.catch(() => {});
    return operation;
  }

  function plan(data, overrides = []) {
    const benefits = data.items.filter(managed);
    const entries = data.items.filter(item => !managed(item) && (item.value > 0 || item.wgAllowed || item.vexValue > 0)).map(item => ({
      item, ref: item.ref, choices: Array(item.quantity).fill(null)
    }));
    for (const line of benefits) {
      const entry = line.properties?._sd_benefit_ref
        ? entries.find(candidate => candidate.ref === line.properties._sd_benefit_ref)
        : entries.find(candidate => candidate.item.product === Number(line.properties?._sd_benefit_product));
      if (!entry) continue;
      const type = benefitType(line);
      if (!allowed(type, entry.item, data)) continue;
      const positions = line.properties?._sd_benefit_ref
        ? String(line.properties._sd_benefit_units || '').split(',').map(Number).slice(0, line.quantity)
        : entry.choices.map((_, index) => index + 1);
      for (const unit of positions) {
        if (Number.isInteger(unit) && unit > 0 && unit <= entry.choices.length && !entry.choices[unit - 1]) entry.choices[unit - 1] = type;
      }
    }
    for (const override of overrides) {
      const entry = entries.find(candidate => candidate.ref === override.ref);
      if (!entry) continue;
      entry.choices = entry.choices.map((_, index) => {
        const type = override.choices[index];
        return allowed(type, entry.item, data) ? type : null;
      });
    }
    const targets = entries.flatMap(entry => ['gift', 'vex', 'white_glove'].flatMap(type => {
      const units = entry.choices.flatMap((choice, index) => choice === type ? [index + 1] : []);
      return units.length ? [{ ref: entry.ref, item: benefitItem(type, type === 'vex' ? entry.item.vexValue : entry.item.value, entry.item, units) }] : [];
    }));
    return { entries, benefits, targets };
  }

  function benefitItem(type, value, parent, units) {
    const properties = {
      'Superbrand benefit': label(type, value, parent),
      '_Superbrand benefit add-on': 'true', '_superbrand_hidden': 'true',
      '_sd_benefit_product': String(parent.product),
      '_sd_benefit_type': type, '_sd_benefit_ref': parent.ref,
      '_sd_benefit_line': parent.key || '', '_sd_benefit_units': units.join(',')
    };
    if (parent.title) properties[config.applies_label] = parent.title;
    return { id: benefitId(type, value), quantity: units.length, properties };
  }

  function matchesTarget(line, target) {
    return Number(line.variant) === target.item.id && line.quantity === target.item.quantity &&
      canonical(line.properties) === canonical(target.item.properties);
  }

  async function settle(data, overrides = []) {
    data = data || await state();
    const decision = plan(data, overrides);
    const desired = decision.targets;
    const used = new Set();
    for (const target of desired) {
      let current = data.items.find(line => managed(line) && !used.has(line.key) && matchesTarget(line, target));
      if (!current) {
        current = data.items.find(line => managed(line) && !used.has(line.key) &&
          Number(line.variant) === target.item.id && (line.properties?._sd_benefit_ref === target.ref ||
          (!line.properties?._sd_benefit_ref && Number(line.properties?._sd_benefit_product) === Number(target.item.properties._sd_benefit_product))));
        let failure;
        try {
          if (current) await request('cart/change.js', { id: current.key, quantity: target.item.quantity, properties: target.item.properties });
          else await request('cart/add.js', { items: [target.item] });
        } catch (error) { failure = error; }
        data = await state();
        current = data.items.find(line => managed(line) && !used.has(line.key) && matchesTarget(line, target));
        if (!current) throw failure || new Error(config.unconfirmed);
      }
      used.add(current.key);
    }
    const updates = {};
    for (const line of data.items.filter(managed)) if (!used.has(line.key)) updates[line.key] = 0;
    if (Object.keys(updates).length) {
      let failure;
      try { await request('cart/update.js', { updates }); } catch (error) { failure = error; }
      data = await state();
      if (data.items.some(line => Object.hasOwn(updates, line.key))) throw failure || new Error(config.unconfirmed);
    }
    const actual = data.items.filter(managed);
    if (actual.length !== desired.length || desired.some(target => !actual.some(line => matchesTarget(line, target)))) throw new Error(config.unconfirmed);
    return data;
  }

  async function reconcile(data, overrides = []) {
    data = data || await state();
    const previous = plan(data).entries.map(entry => ({ ref: entry.ref, choices: [...entry.choices] }));
    try {
      return await settle(data, overrides);
    } catch (error) {
      if (overrides.length) {
        try { await settle(await state(), previous); } catch (_) {}
      }
      await refresh().catch(() => {});
      throw error;
    }
  }

  async function withoutBenefits() {
    return run(async () => {
      const cart = await request('cart.js');
      const markers = cart.items.filter(item => managedIds.has(Number(item.variant_id || item.variant || item.id)));
      if (markers.length) {
        let failure;
        try { await request('cart/update.js', { updates: Object.fromEntries(markers.map(item => [item.key, 0])) }); }
        catch (error) { failure = error; }
        const confirmed = await request('cart.js');
        if (confirmed.items.some(item => managedIds.has(Number(item.variant_id || item.variant || item.id)))) throw failure || new Error(config.unconfirmed);
      }
      selected.clear();
      await refresh().catch(() => {});
    });
  }

  async function refresh() {
    const cart = document.querySelector('cart-items');
    if (cart?.getCart) {
      let timer;
      try {
        await Promise.race([cart.getCart({ silent: true }), new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(config.unconfirmed)), 12000); })]);
      } finally { clearTimeout(timer); }
    }
  }

  function productItem(formData) {
    const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1), properties: {} };
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) item.properties[match[1]] = value;
    }
    if (formData.get('selling_plan')) item.selling_plan = formData.get('selling_plan');
    return item;
  }

  function variantRecords(panel) {
    try {
      const node = panel.querySelector('script[data-sd-benefit-variants]');
      const data = node ? JSON.parse(node.textContent) : {};
      return Array.isArray(data) ? Object.fromEntries(data.map(item => [String(item.id), item])) : data;
    } catch (_) { return {}; }
  }

  function selectionKey(panel, variant) {
    return panel.dataset.sdBenefitProduct + ':' + variant;
  }

  function productPanels() {
    return document.querySelectorAll('[data-sd-benefit-product]:not([data-sd-benefit-cart-item])');
  }

  function syncPanels(variant, target) {
    for (const panel of productPanels()) {
      const records = variantRecords(panel);
      const section = panel.closest('[data-section-type="product"], [data-product]');
      if (variant && records[String(variant)] && (!target || !section || section.contains(target))) panel.dataset.sdVariantId = String(variant);
      const formVariant = section?.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      const id = String(variant && records[String(variant)] ? panel.dataset.sdVariantId : formVariant || panel.dataset.sdVariantId || '');
      const item = records[id];
      if (!item) continue;
      panel.dataset.sdVariantId = id;
      panel.dataset.sdBenefitValue = item.value;
      const key = selectionKey(panel, id);
      const chosen = selected.get(key);
      if (chosen && !allowed(chosen, item, latestCart || {}, true)) selected.delete(key);
      let visible = 0;
      panel.querySelectorAll('[data-sd-benefit-choice]').forEach(button => {
        const type = button.dataset.sdBenefitChoice;
        const available = allowed(type, item, latestCart || {}, true);
        const value = type === 'vex' ? item.vexValue : item.value;
        button.hidden = !available;
        button.disabled = !available;
        button.dataset.sdBenefitValue = String(value || 0);
        button.setAttribute('aria-pressed', String(selected.get(key) === type));
        if (available) {
          visible++;
          const name = button.querySelector('.sd-benefit-choice__name');
          if (name) name.textContent = type === 'white_glove' ? label(type, value, item) : String(type === 'vex' ? config.vex_option || config.vex_label : config.gift_option || config.gift_label).replace('{value}', value);
        }
      });
      panel.hidden = visible === 0;
      const previewNote = panel.querySelector('[data-sd-benefit-preview-note]');
      if (previewNote) previewNote.hidden = !(config.vexPreview === true && item.vexPreviewAllowed === true && !allowed('vex', item, latestCart || {}));
    }
  }

  function choiceFor(variant, data = {}) {
    for (const panel of productPanels()) {
      const item = variantRecords(panel)[String(variant)];
      if (!item) continue;
      const type = selected.get(selectionKey(panel, variant));
      if (type && allowed(type, item, data)) return { type, product: Number(panel.dataset.sdBenefitProduct) };
    }
    return null;
  }

  function retainStockCapture(item, cart) {
    const timestamp = '_SD stock captured at UTC';
    if (!item.properties[timestamp]) return;
    const signature = properties => JSON.stringify(Object.entries(properties || {})
      .filter(([key, value]) => key !== timestamp && value !== '' && value != null)
      .sort(([a], [b]) => a.localeCompare(b)));
    const incoming = signature(item.properties);
    const existing = cart.items.find(line => line.variant === item.id &&
      String(line.selling_plan || '') === String(item.selling_plan || '') &&
      line.properties?.[timestamp] && signature(line.properties) === incoming);
    if (existing) item.properties[timestamp] = existing.properties[timestamp];
  }

  async function add(formData) {
    return run(async () => {
      const item = productItem(formData);
      if (managedIds.has(item.id)) throw new Error(config.choose_with_product);
      if (!Number.isInteger(item.quantity) || item.quantity < 1) throw new Error(config.failed);
      const before = await state();
      retainStockCapture(item, before);
      const ref = await itemReference(item.id, item.properties, item.selling_plan);
      const previous = plan(before).entries.find(entry => entry.ref === ref);
      const existing = before.items.find(line => line.ref === ref);
      const preference = choiceFor(item.id, before);
      const oldQuantity = existing?.quantity || 0;
      const choices = previous ? [...previous.choices] : Array(oldQuantity).fill(null);
      choices.push(...Array(item.quantity).fill(preference?.type || null));
      let result, failure;
      try { result = await request('cart/add.js', { items: [item] }); } catch (error) { failure = error; }
      let after = await state();
      const newQuantity = after.items.filter(line => line.ref === ref).reduce((sum, line) => sum + line.quantity, 0);
      if (newQuantity < oldQuantity + item.quantity) {
        const cleanup = {};
        after.items.filter(managed).forEach(line => {
          if (!before.items.some(old => old.key === line.key)) cleanup[line.key] = 0;
        });
        if (Object.keys(cleanup).length) await request('cart/update.js', { updates: cleanup });
        await reconcile();
        throw failure || new Error(config.failed);
      }
      try { after = await reconcile(after, [{ ref, choices }]); }
      catch (error) { throw new Error(config.benefit_failed || error.message); }
      const added = result?.items?.find(addedItem => Number(addedItem.variant_id || addedItem.id) === item.id);
      const confirmed = added || (await request('cart.js')).items.find(addedItem => addedItem.variant_id === item.id);
      if (!confirmed) throw new Error(config.unconfirmed);
      return new Response(JSON.stringify(confirmed), { headers: { 'Content-Type': 'application/json' } });
    });
  }

  async function change(body) {
    return run(async () => {
      const before = await state();
      const line = before.items.find(item => item.key === body.id);
      if (line && body.properties) body = { ...body, properties: { ...line.properties, ...body.properties } };
      const allocation = plan(before);
      let overrides = [];
      if (line && !managed(line)) {
        const entry = allocation.entries.find(candidate => candidate.ref === line.ref);
        if (entry) {
          const quantity = body.quantity === undefined ? line.quantity : Number(body.quantity);
          if (!Number.isInteger(quantity) || quantity < 0) throw new Error(config.failed);
          const choices = entry.choices.slice(0, quantity);
          const uniform = entry.choices.length && entry.choices.every(type => type && type === entry.choices[0]) ? entry.choices[0] : null;
          while (choices.length < quantity) choices.push(uniform);
          const ref = body.properties ? await itemReference(line.variant, body.properties, line.selling_plan) : entry.ref;
          overrides = [{ ref, choices }];
        }
      } else if (line && managed(line) && body.quantity === 0) {
        const entry = allocation.entries.find(candidate => candidate.ref === line.properties?._sd_benefit_ref ||
          (!line.properties?._sd_benefit_ref && candidate.item.product === Number(line.properties?._sd_benefit_product)));
        if (entry) {
          const units = line.properties?._sd_benefit_ref ? String(line.properties._sd_benefit_units).split(',').map(Number) : entry.choices.map((_, index) => index + 1);
          overrides = [{ ref: entry.ref, choices: entry.choices.map((type, index) => units.includes(index + 1) ? null : type) }];
        }
      }
      let failure;
      try { await request('cart/change.js', body); } catch (error) { failure = error; }
      const after = await state();
      if (failure) {
        const expectedRef = overrides[0]?.ref || line?.ref;
        const changed = managed(line || {}) ? !after.items.some(item => item.key === line?.key) : after.items.find(item => item.ref === expectedRef);
        if (Number(body.quantity) !== 0 && (!changed || (body.quantity !== undefined && changed.quantity !== Number(body.quantity)))) throw failure;
        if (Number(body.quantity) === 0 && after.items.some(item => item.key === line?.key)) throw failure;
      }
      await reconcile(after, overrides);
      return new Response(JSON.stringify(await request('cart.js')), { headers: { 'Content-Type': 'application/json' } });
    });
  }

  async function choose(lineKey, unit, type) {
    return run(async () => {
      const data = await state();
      const entry = plan(data).entries.find(candidate => candidate.item.key === lineKey);
      if (!entry || unit < 1 || unit > entry.choices.length) throw new Error(config.no_qualifier);
      if (type && !allowed(type, entry.item, data)) throw new Error(config.no_longer_available || config.no_qualifier);
      entry.choices[unit - 1] = type;
      await reconcile(data, [{ ref: entry.ref, choices: entry.choices }]);
      await refresh();
    });
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-sd-benefit-choice]');
    if (!button || button.disabled || button.hidden) return;
    event.preventDefault();
    const panel = button.closest('[data-sd-benefit-product]');
    const type = button.dataset.sdBenefitChoice;
    if (panel.hasAttribute('data-sd-benefit-cart-item')) {
      if (pending) return;
      panel.querySelectorAll('button').forEach(control => { control.disabled = true; });
      const lineKey = panel.dataset.sdBenefitKey;
      const unit = Number(button.closest('[data-sd-benefit-unit]').dataset.sdBenefitUnit);
      choose(lineKey, unit, type).then(() => {
        const replacement = Array.from(document.querySelectorAll('[data-sd-benefit-cart-item]')).find(node => node.dataset.sdBenefitKey === lineKey);
        const focusTarget = replacement?.open ? replacement.querySelector('[data-sd-benefit-unit="' + unit + '"] [data-sd-benefit-choice="' + type + '"]') : replacement?.querySelector('summary');
        focusTarget?.focus({ preventScroll: true });
      }).catch(() => { panel.querySelectorAll('button').forEach(control => { control.disabled = false; }); });
    } else {
      const variant = panel.dataset.sdVariantId;
      const item = variantRecords(panel)[String(variant)];
      if (!item || !allowed(type, item, latestCart || {}, true)) return;
      const key = selectionKey(panel, variant);
      if (selected.get(key) === type) selected.delete(key); else selected.set(key, type);
      panel.querySelectorAll('[data-sd-benefit-choice]').forEach(control => control.setAttribute('aria-pressed', String(control === button)));
      const status = panel.querySelector('[data-sd-benefit-status]');
      if (status) {
        status.hidden = !selected.has(key);
        status.textContent = type === 'vex' && !allowed(type, item, latestCart || {}) ? config.preview_only : config.selected;
      }
      syncPanels();
    }
  });

  async function checkout(event, control) {
    if (checkoutPass) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (pending) return;
    try {
      await run(async () => { await reconcile(); await refresh(); });
      const holder = document.querySelector('[data-cart-checkout-button]') || control;
      const current = holder.matches('button,a') ? holder : holder.querySelector('button[name=checkout]');
      if (!current) throw new Error(config.unconfirmed);
      checkoutPass = true; current.click(); checkoutPass = false;
    } catch (_) { checkoutPass = false; }
  }
  document.addEventListener('click', event => {
    const control = event.target.closest('[data-cart-checkout-button],a[href="/checkout"]');
    if (control) checkout(event, control);
  }, true);
  document.addEventListener('submit', event => {
    if (!checkoutPass && event.target.matches('[data-cart-form]') && event.submitter?.name === 'checkout') checkout(event, event.submitter);
  }, true);
  document.addEventListener('theme:cart:change', () => { gate(); syncPanels(); });
  document.addEventListener('theme:variant:change', event => syncPanels(event.detail?.variant?.id || event.detail?.id, event.target));
  document.addEventListener('shopify:section:load', () => syncPanels());
  document.addEventListener('change', event => {
    if (event.target.matches?.('[name="id"], [name^="options["]')) syncPanels(event.target.name === 'id' ? event.target.value : null, event.target);
  });
  document.addEventListener('change', event => {
    const field = event.target;
    if (!field.matches?.('textarea[form="cartForm"]')) return;
    const attribute = field.name.match(/^attributes\[(.+)\]$/);
    if (field.name !== 'note' && !attribute) return;
    const body = attribute ? { attributes: { [attribute[1]]: field.value } } : { note: field.value };
    run(() => request('cart/update.js', body)).catch(() => {});
  });
  window.addEventListener('pageshow', event => { if (event.persisted) run(async () => { await reconcile(); await refresh(); }).catch(() => {}); });
  window.sdBenefits = { add, change, choose, reconcile, plan, withoutBenefits, allowed, syncPanels };
  syncPanels();
  if (document.querySelector('[data-sd-benefit-cart], [data-sd-benefit-line]')) run(async () => { await reconcile(); await refresh(); }).catch(() => {});
})();

